import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { roleLabel } from '@fsv/core';
import { writeLastTenant } from '../auth/lastTenant';
import type { MembershipRef } from '@fsv/data';
import { tx } from '@fsv/data';
import { switchTenantPath } from '../lib/tenantRouting';
import { ClubLogo } from './ClubLogo';
import { Icon } from './Icon';

/**
 * Tenant name in the top bar. With more than one membership it opens a menu
 * of the person's clubs; picking one keeps the section (search, inbox, …) and
 * drops ids that belong to the old club.
 */
export function TenantSwitcher({ current, memberships }: { current: MembershipRef; memberships: MembershipRef[] }) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => setOpen(false), [location.pathname]);

  if (memberships.length < 2) {
    return (
      <span className="tenant-switch tenant-switch--static" title={tx.tenant.current}>
        <ClubLogo club={current} size={28} />
        <span className="tenant-switch__name">{current.name}</span>
      </span>
    );
  }

  return (
    <div className="tenant-switch" ref={ref}>
      <button
        ref={buttonRef}
        type="button"
        className="tenant-switch__button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`${tx.tenant.switcher}: ${current.name}`}
        onClick={() => setOpen((o) => !o)}
      >
        <ClubLogo club={current} size={28} />
        <span className="tenant-switch__name">{current.name}</span>
        <Icon name="down" size={18} />
      </button>
      {open && (
        <div id={menuId} className="tenant-switch__menu" role="group" aria-label={tx.tenant.switcher}>
          <p className="tenant-switch__head label">{tx.tenant.switcher}</p>
          <ul>
            {memberships.map((m) => {
              const isCurrent = m.slug === current.slug;
              return (
                <li key={m.tenantId}>
                  <Link
                    to={switchTenantPath(location.pathname, m.slug)}
                    className={`tenant-switch__item${isCurrent ? ' is-current' : ''}`}
                    aria-current={isCurrent ? 'true' : undefined}
                    onClick={() => writeLastTenant(m.slug)}
                  >
                    <span className="tenant-switch__check" aria-hidden="true">
                      {isCurrent && <Icon name="check" size={18} />}
                    </span>
                    <ClubLogo club={m} size={32} />
                    <span className="tenant-switch__text">
                      <span className="tenant-switch__item-name">{m.name}</span>
                      <span className="tenant-switch__meta">
                        <span className="mono">{m.slug}</span> · {roleLabel[m.role]}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
          <Link to="/vereine" className="tenant-switch__all">
            <Icon name="club" size={18} />
            {tx.tenant.allClubs}
          </Link>
        </div>
      )}
    </div>
  );
}
